import { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useTheme } from '../../../../contexts/theme-context';
import { useResponsive } from '../../../../hooks/useResponsive';
import { supabaseApi } from '../../../../lib/api/supabase';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/utils';
import { Play, Database, Table, Key, MoreVertical, RefreshCw, Download, ChevronLeft, ChevronRight, Plus, Check, X, FileCode, PanelLeftClose, PanelLeft, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';

interface DatabaseTabProps {
  projectId: string;
}

export function DatabaseTab({ projectId }: DatabaseTabProps) {
  const { token } = useAuthToken();
  const { resolvedTheme } = useTheme();
  const { isMobile } = useResponsive();
  const [sql, setSql] = useState('SELECT * FROM users LIMIT 10;');
  const [showSidebar, setShowSidebar] = useState(!isMobile); // Hidden by default on mobile
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [tables, setTables] = useState<any[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [monacoRef, setMonacoRef] = useState<any>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Selection state
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Samples modal state
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [creatingSamples, setCreatingSamples] = useState(false);

  // Cell editing state
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnKey: string; value: any } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Realtime state
  const [realtimeTables, setRealtimeTables] = useState<Set<string>>(new Set());
  const [togglingRealtime, setTogglingRealtime] = useState<string | null>(null);

  // Add row modal state
  const [showAddRowModal, setShowAddRowModal] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newRowData, setNewRowData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (token) {
      loadTables();
      loadRealtimeTables();
    }
  }, [projectId, token]);

  const loadRealtimeTables = async () => {
    if (!token) return;
    try {
      const response = await supabaseApi.getRealtimeTables(token, projectId);
      setRealtimeTables(new Set(response.tables || []));
    } catch (error) {
      // Silently fail - realtime status is optional
      console.error('Failed to load realtime tables:', error);
    }
  };

  const handleToggleRealtime = async (tableName: string) => {
    if (!token) return;
    const isEnabled = realtimeTables.has(tableName);
    setTogglingRealtime(tableName);

    try {
      await supabaseApi.setTableRealtime(token, projectId, tableName, !isEnabled);
      // Update local state
      setRealtimeTables(prev => {
        const next = new Set(prev);
        if (isEnabled) {
          next.delete(tableName);
        } else {
          next.add(tableName);
        }
        return next;
      });
      toast.success(`Realtime ${isEnabled ? 'disabled' : 'enabled'} for ${tableName}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to toggle realtime: ${errorMessage}`);
    } finally {
      setTogglingRealtime(null);
    }
  };

  const loadTables = async () => {
    if (!token) return;
    setLoadingTables(true);
    try {
      const response = await supabaseApi.getTables(token, projectId);
      // Filter to only show public schema tables
      const publicTables = (response.tables || []).filter(
        (table: any) => table.schema_name === 'public'
      );
      setTables(publicTables);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load tables: ${errorMessage}`);
    } finally {
      setLoadingTables(false);
    }
  };

  const executeQuery = async () => {
    if (!token || !sql.trim()) return;

    setExecuting(true);
    setResult(null);

    try {
      const response = await supabaseApi.executeQuery(token, projectId, sql);
      setResult(response);
      toast.success(`Query executed successfully (${response.rowCount} rows)`);
    } catch (error: unknown) {
      // Extract error message from axios response or fall back to generic message
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.message || axiosError.message || 'Unknown error';
      toast.error(`Query failed: ${errorMessage}`);
      setResult({ error: errorMessage });
    } finally {
      setExecuting(false);
    }
  };

  const handleTableClick = async (tableName: string) => {
    const query = `SELECT * FROM ${tableName} LIMIT 10;`;
    setSql(query);

    if (!token) return;
    setExecuting(true);
    setResult(null);
    try {
      const response = await supabaseApi.executeQuery(token, projectId, query);
      setResult(response);
      toast.success(`Query executed successfully (${response.rowCount} rows)`);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.message || axiosError.message || 'Unknown error';
      toast.error(`Query failed: ${errorMessage}`);
      setResult({ error: errorMessage });
    } finally {
      setExecuting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!token) return;

    setResettingPassword(true);
    try {
      const result = await supabaseApi.resetDatabasePassword(token, projectId);
      toast.success('Database password has been reset successfully. Your connection string has been updated.');
      setShowResetPasswordModal(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to reset password: ${errorMessage}`);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleAddSamplesTable = async () => {
    if (!token) return;

    setCreatingSamples(true);
    try {
      // Create table
      await supabaseApi.executeQuery(
        token,
        projectId,
        `CREATE TABLE IF NOT EXISTS samples (
          id SERIAL PRIMARY KEY,
          features VARCHAR(255),
          description TEXT
        )`
      );

      // Insert sample data
      await supabaseApi.executeQuery(
        token,
        projectId,
        `INSERT INTO samples (features, description) VALUES
        ('AI-Powered Development', 'Build applications with intelligent code assistance and automated workflows'),
        ('Real-Time Collaboration', 'Multiple users can edit and preview changes simultaneously with instant sync'),
        ('One-Click Deployment', 'Deploy your applications to production with automated builds and zero downtime')`
      );

      toast.success('Samples table created with 3 rows');
      setShowSamplesModal(false);

      // Refresh table list and execute query to show results
      await loadTables();
      setSql('SELECT * FROM samples;');

      // Execute the query to show results immediately
      const response = await supabaseApi.executeQuery(token, projectId, 'SELECT * FROM samples;');
      setResult(response);
      setCurrentPage(1);
      setSelectedRows(new Set());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to create samples table: ${errorMessage}`);
    } finally {
      setCreatingSamples(false);
    }
  };

  const handleRefresh = () => {
    executeQuery();
  };

  // Extract table name from SQL (simple parsing)
  const getTableNameFromSQL = (): string | null => {
    const match = sql.match(/FROM\s+(\w+)/i);
    return match ? match[1] : null;
  };

  const handleCellDoubleClick = (rowIndex: number, columnKey: string, value: any) => {
    setEditingCell({ rowIndex, columnKey, value });
    setEditValue(value === null ? '' : String(value));
  };

  const handleCellSave = async () => {
    if (!editingCell || !token) return;

    const tableName = getTableNameFromSQL();
    if (!tableName) {
      toast.error('Cannot determine table name from query');
      return;
    }

    const row = result.rows[editingCell.rowIndex];
    const rowId = row.id;

    if (!rowId) {
      toast.error('Row must have an id column to edit');
      return;
    }

    try {
      await supabaseApi.updateCell(
        token,
        projectId,
        tableName,
        rowId,
        editingCell.columnKey,
        editValue || null,
      );
      toast.success('Cell updated');
      setEditingCell(null);
      executeQuery(); // Refresh data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to update cell: ${errorMessage}`);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleAddRowClick = () => {
    if (!result?.rows || result.rows.length === 0) {
      toast.error('No table data to determine columns');
      return;
    }

    // Initialize new row data with empty strings for all columns except id
    const columns = Object.keys(result.rows[0]).filter(key => key !== 'id');
    const initialData: Record<string, any> = {};
    columns.forEach(col => {
      initialData[col] = '';
    });
    setNewRowData(initialData);
    setShowAddRowModal(true);
  };

  const handleAddRow = async () => {
    if (!token) return;

    const tableName = getTableNameFromSQL();
    if (!tableName) {
      toast.error('Cannot determine table name from query');
      return;
    }

    setAddingRow(true);
    try {
      await supabaseApi.addRow(token, projectId, tableName, newRowData);
      toast.success('Row added');
      setShowAddRowModal(false);
      setNewRowData({});
      executeQuery(); // Refresh data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to add row: ${errorMessage}`);
    } finally {
      setAddingRow(false);
    }
  };

  const handleCopySQLInsert = (onlySelected: boolean = false) => {
    if (!result?.rows || result.rows.length === 0) {
      toast.error('No data to export');
      return;
    }

    const tableName = getTableNameFromSQL();
    if (!tableName) {
      toast.error('Cannot determine table name from query');
      return;
    }

    const rowsToExport = onlySelected
      ? result.rows.filter((_: any, idx: number) => selectedRows.has(idx))
      : result.rows;

    if (rowsToExport.length === 0) {
      toast.error('No rows to export');
      return;
    }

    const columns = Object.keys(rowsToExport[0]);
    const insertStatements = rowsToExport.map((row: any) => {
      const values = columns.map(col => {
        const value = row[col];
        if (value === null) return 'NULL';
        if (typeof value === 'number') return String(value);
        if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
        // Escape single quotes and wrap in quotes
        return `'${String(value).replace(/'/g, "''")}'`;
      });
      return `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')});`;
    }).join('\n');

    navigator.clipboard.writeText(insertStatements);
    toast.success(`Copied ${rowsToExport.length} INSERT statement${rowsToExport.length !== 1 ? 's' : ''}`);
  };

  // Export helper functions
  const exportToJSON = (rows: any[], filename: string) => {
    const json = JSON.stringify(rows, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToCSV = (rows: any[], filename: string) => {
    if (rows.length === 0) return;

    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null) return '';
          const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
          // Escape quotes and wrap in quotes if contains comma or quote
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ];

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToXLSX = (rows: any[], filename: string) => {
    if (rows.length === 0) return;

    // Simple TSV (Tab-Separated Values) that Excel can open
    const headers = Object.keys(rows[0]);
    const tsvRows = [
      headers.join('\t'),
      ...rows.map(row =>
        headers.map(header => {
          const value = row[header];
          if (value === null) return '';
          return typeof value === 'object' ? JSON.stringify(value) : String(value);
        }).join('\t')
      )
    ];

    const tsv = tsvRows.join('\n');
    const blob = new Blob([tsv], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = (format: 'json' | 'csv' | 'xlsx') => {
    if (!result?.rows || result.rows.length === 0) {
      toast.error('No data to export');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `table-export-${timestamp}.${format}`;

    switch (format) {
      case 'json':
        exportToJSON(result.rows, filename);
        break;
      case 'csv':
        exportToCSV(result.rows, filename);
        break;
      case 'xlsx':
        exportToXLSX(result.rows, filename);
        break;
    }

    toast.success(`Exported ${result.rows.length} rows to ${format.toUpperCase()}`);
  };

  const handleExportSelected = (format: 'json' | 'csv' | 'xlsx') => {
    if (!result?.rows || selectedRows.size === 0) {
      toast.error('No rows selected');
      return;
    }

    const selectedData = result.rows.filter((_: any, idx: number) => selectedRows.has(idx));
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `table-export-selected-${timestamp}.${format}`;

    switch (format) {
      case 'json':
        exportToJSON(selectedData, filename);
        break;
      case 'csv':
        exportToCSV(selectedData, filename);
        break;
      case 'xlsx':
        exportToXLSX(selectedData, filename);
        break;
    }

    toast.success(`Exported ${selectedData.length} selected rows to ${format.toUpperCase()}`);
  };

  const toggleRowSelection = (idx: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(idx)) {
      newSelection.delete(idx);
    } else {
      newSelection.add(idx);
    }
    setSelectedRows(newSelection);
  };

  const toggleSelectAll = () => {
    if (!result?.rows) return;

    if (selectedRows.size === paginatedRows.length) {
      setSelectedRows(new Set());
    } else {
      const allIndices = paginatedRows.map((_: any, idx: number) =>
        (currentPage - 1) * rowsPerPage + idx
      );
      setSelectedRows(new Set(allIndices));
    }
  };

  // Pagination calculations
  const totalRows = result?.rows?.length || 0;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startRow = (currentPage - 1) * rowsPerPage + 1;
  const endRow = Math.min(currentPage * rowsPerPage, totalRows);
  const paginatedRows = result?.rows?.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) || [];

  // Reset to page 1 when results change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows(new Set());
  }, [result]);

  // Sync Monaco editor theme when app theme changes
  useEffect(() => {
    if (!monacoRef) return;
    if (resolvedTheme === 'dark') {
      monacoRef.editor.defineTheme('sql-dark', {
        base: 'vs-dark',
        inherit: true,
        rules: [],
        colors: {
          'editor.background': '#19191e',
          'editor.lineHighlightBackground': '#ffffff08',
          'editor.lineHighlightBorder': '#00000000',
        },
      });
      monacoRef.editor.setTheme('sql-dark');
    } else {
      monacoRef.editor.defineTheme('sql-light', {
        base: 'vs',
        inherit: true,
        rules: [],
        colors: {
          'editor.lineHighlightBackground': '#00000006',
          'editor.lineHighlightBorder': '#00000000',
        },
      });
      monacoRef.editor.setTheme('sql-light');
    }
  }, [resolvedTheme, monacoRef]);

  return (
    <div className="flex flex-col h-full overflow-hidden rounded-lg border border-border/60">
      {/* Mobile Header with Table Selector */}
      {isMobile && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40 bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSidebar(!showSidebar)}
            className="h-8 px-2"
          >
            {showSidebar ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
          </Button>
          <span className="text-sm font-medium flex-1">Tables ({tables.length})</span>
          <Button
            onClick={executeQuery}
            disabled={executing || !sql.trim()}
            size="sm"
            className="h-8"
          >
            <Play className="w-3.5 h-3.5 mr-1" />
            Run
          </Button>
        </div>
      )}

      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        {/* Mobile: Overlay backdrop when sidebar open */}
        {isMobile && showSidebar && (
          <div
            className="absolute inset-0 bg-black/40 z-10"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* Table Browser Sidebar - Overlay on mobile, inline on desktop */}
        <div className={cn(
          "flex-shrink-0 border-r border-border/40 flex flex-col bg-card transition-all",
          isMobile
            ? cn(
                "absolute left-0 top-0 bottom-0 z-20 shadow-xl",
                showSidebar ? "w-56" : "w-0 overflow-hidden border-r-0"
              )
            : "w-52 bg-muted/10"
        )}>
          {(!isMobile || showSidebar) && (
            <>
              <div className="px-3 h-11 border-b border-border/40 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tables</span>
                <div className="flex items-center gap-0.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 hover:bg-muted rounded transition-colors">
                        <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setShowSamplesModal(true)}>
                        <Database className="w-4 h-4 mr-2" />
                        Add Samples Table
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    onClick={loadTables}
                    disabled={loadingTables}
                    className="p-1 hover:bg-muted rounded transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground ${loadingTables ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-1.5">
                {loadingTables ? (
                  <div className="px-2 py-3 text-xs text-muted-foreground">Loading...</div>
                ) : tables.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-muted-foreground">No tables found</div>
                ) : (
                  <div className="space-y-0.5">
                    {tables.map((table) => (
                      <div
                        key={`${table.schema_name}.${table.table_name}`}
                        className="group flex items-center rounded hover:bg-muted/50 transition-colors"
                      >
                        <button
                          onClick={() => {
                            handleTableClick(table.table_name);
                            if (isMobile) setShowSidebar(false);
                          }}
                          className="flex-1 text-left px-2.5 py-1.5 flex items-center gap-2 text-[13px]"
                        >
                          <Table className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          <span className="truncate font-medium">{table.table_name}</span>
                          {realtimeTables.has(table.table_name) && (
                            <span title="Realtime enabled">
                              <Zap className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                            </span>
                          )}
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="p-1.5 hover:bg-muted rounded transition-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVertical className="w-3 h-3 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleToggleRealtime(table.table_name)}
                              disabled={togglingRealtime === table.table_name}
                            >
                              <Zap className={cn(
                                "w-4 h-4 mr-2",
                                realtimeTables.has(table.table_name) ? "text-yellow-500" : "text-muted-foreground"
                              )} />
                              {togglingRealtime === table.table_name
                                ? 'Updating...'
                                : realtimeTables.has(table.table_name)
                                  ? 'Disable Realtime'
                                  : 'Enable Realtime'
                              }
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

      {/* Main: Editor + Results */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Editor Toolbar - Desktop only */}
        {!isMobile && (
          <div className="px-3 h-11 border-b border-border/40 flex items-center justify-end bg-background">
            <Button
              onClick={executeQuery}
              disabled={executing || !sql.trim()}
              size="sm"
              className="h-7 text-xs px-3 flex items-center gap-1.5"
            >
              <Play className="w-3.5 h-3.5" />
              {executing ? 'Running...' : 'Run'}
            </Button>
          </div>
        )}

        {/* Monaco Editor */}
        <div className={cn("flex-shrink-0", isMobile ? "h-32" : "h-48")}>
          <Editor
            height="100%"
            defaultLanguage="sql"
            value={sql}
            onChange={(value) => setSql(value || '')}
            theme={resolvedTheme === 'dark' ? 'vs-dark' : 'vs-light'}
            options={{
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              padding: { top: 8 },
            }}
            onMount={(_editor, monaco) => {
              setMonacoRef(monaco);
            }}
          />
        </div>

        {/* Results */}
        {result && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Results toolbar */}
            <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between bg-muted/20">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  {result.rowCount !== undefined
                    ? `${result.rowCount} row${result.rowCount !== 1 ? 's' : ''}`
                    : 'Results'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {!isMobile && (
                  <Button
                    onClick={handleAddRowClick}
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs px-2.5 flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Row
                  </Button>
                )}
                <button
                  onClick={handleRefresh}
                  className="p-1.5 hover:bg-muted rounded transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 hover:bg-muted rounded transition-colors">
                      <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExportAll('json')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export all to .json
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('csv')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export all to .csv
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportAll('xlsx')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export all to .xlsx
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleCopySQLInsert(false)}>
                      <FileCode className="w-4 h-4 mr-2" />
                      Copy as SQL
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleExportSelected('json')}
                      disabled={selectedRows.size === 0}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export selected to .json
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportSelected('csv')}
                      disabled={selectedRows.size === 0}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export selected to .csv
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportSelected('xlsx')}
                      disabled={selectedRows.size === 0}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export selected to .xlsx
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleCopySQLInsert(true)}
                      disabled={selectedRows.size === 0}
                    >
                      <FileCode className="w-4 h-4 mr-2" />
                      Copy Selected as SQL
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {result.error ? (
              <div className="p-4">
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3.5">
                  <p className="text-destructive font-medium text-sm">Error</p>
                  <p className="text-destructive/80 text-sm mt-1">{result.error}</p>
                </div>
              </div>
            ) : result.rows && result.rows.length > 0 ? (
              <div
                className="flex-1 overflow-y-auto"
                style={{ overflowX: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#a0a0a0 transparent' }}
              >
                <table className="min-w-full">
                  <thead className="bg-background border-b border-border/40 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedRows.size > 0 && selectedRows.size === paginatedRows.length}
                          onChange={toggleSelectAll}
                          className="rounded border-border"
                        />
                      </th>
                      {Object.keys(result.rows[0]).map((key) => (
                        <th
                          key={key}
                          className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                        >
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {paginatedRows.map((row: any, idx: number) => {
                      const rowIndex = (currentPage - 1) * rowsPerPage + idx;
                      const isSelected = selectedRows.has(rowIndex);
                      return (
                        <tr
                          key={idx}
                          className={`hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''}`}
                        >
                          <td className="px-3 py-1.5 w-10">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRowSelection(rowIndex)}
                              className="rounded border-border"
                            />
                          </td>
                          {Object.keys(row).map((columnKey, cellIdx) => {
                            const value = row[columnKey];
                            const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnKey === columnKey;

                            return (
                              <td
                                key={cellIdx}
                                className="px-3 py-1.5 text-sm text-foreground whitespace-nowrap"
                                onDoubleClick={() => handleCellDoubleClick(rowIndex, columnKey, value)}
                                title={isEditing ? 'Editing...' : String(value)}
                              >
                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleCellSave();
                                        if (e.key === 'Escape') handleCellCancel();
                                      }}
                                      autoFocus
                                      className="flex-1 px-2 py-0.5 border border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary bg-background text-foreground text-sm"
                                    />
                                    <button
                                      onClick={handleCellSave}
                                      className="p-1 hover:bg-primary/10 rounded text-primary"
                                      title="Save (Enter)"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={handleCellCancel}
                                      className="p-1 hover:bg-destructive/10 rounded text-destructive"
                                      title="Cancel (Esc)"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="truncate cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1">
                                    {value === null ? (
                                      <span className="text-muted-foreground italic">null</span>
                                    ) : typeof value === 'object' ? (
                                      JSON.stringify(value)
                                    ) : (
                                      String(value)
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4">
                <p className="text-muted-foreground text-sm">No rows returned</p>
              </div>
            )}

            {/* Pagination */}
            {result.rows && result.rows.length > 0 && (
              <div className={cn(
                "px-3 py-2 border-t border-border/40 flex items-center bg-muted/10",
                isMobile ? "justify-center gap-2" : "justify-between"
              )}>
                {!isMobile && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {startRow}-{endRow} of {totalRows}
                    </span>
                    <select
                      id="rowsPerPage"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="h-6 px-1.5 border border-border/60 rounded text-xs bg-background text-foreground"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {isMobile ? (
                    <span className="text-xs text-muted-foreground px-2">
                      {currentPage} / {totalPages}
                    </span>
                  ) : (
                    Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-6 h-6 rounded text-xs ${
                            currentPage === pageNum
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })
                  )}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      </div>

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowResetPasswordModal(false)}>
          <div
            className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4 border border-border/60"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Key className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold">Reset Database Password</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Are you sure you want to reset your database password? This will generate a new password and update your connection string.
                <br /><br />
                <strong className="text-foreground">Warning:</strong> Any applications using the old password will lose database access until updated.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setShowResetPasswordModal(false)}
                  disabled={resettingPassword}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  variant="destructive"
                  size="sm"
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Samples Table Modal */}
      {showSamplesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSamplesModal(false)}>
          <div
            className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4 border border-border/60"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Database className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Add Samples Table</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Creates a "samples" table with 3 columns (id, features, description) and 3 sample rows.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setShowSamplesModal(false)}
                  disabled={creatingSamples}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddSamplesTable}
                  disabled={creatingSamples}
                  size="sm"
                >
                  {creatingSamples ? 'Creating...' : 'Create Table'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Row Modal */}
      {showAddRowModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddRowModal(false)}>
          <div
            className="bg-card rounded-lg shadow-xl max-w-lg w-full mx-4 border border-border/60"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-4">Add New Row</h3>
              <div className="space-y-3 mb-5 max-h-96 overflow-y-auto">
                {Object.keys(newRowData).map((column) => (
                  <div key={column}>
                    <label className="block text-sm font-medium mb-1">
                      {column}
                    </label>
                    <Input
                      type="text"
                      value={newRowData[column] || ''}
                      onChange={(e) => setNewRowData({ ...newRowData, [column]: e.target.value })}
                      placeholder={`Enter ${column}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setShowAddRowModal(false)}
                  disabled={addingRow}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddRow}
                  disabled={addingRow}
                  size="sm"
                >
                  {addingRow ? 'Adding...' : 'Add Row'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
